// Datumhelpers voor de kalender. De TRS-kolom `datum` is een date-only veld
// (opgeslagen als UTC-middernacht), dus we formatteren bewust in UTC om te
// voorkomen dat een datum een dag verschuift.
const TZ = "UTC";

export function dagNummer(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", { day: "numeric", timeZone: TZ }).format(d);
}

export function maandKort(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", { month: "short", timeZone: TZ })
    .format(d)
    .replace(".", "");
}

export function weekdagKort(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", { weekday: "short", timeZone: TZ })
    .format(d)
    .replace(".", "");
}

export function maandJaarLang(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", { month: "long", year: "numeric", timeZone: TZ }).format(d);
}

export function volledigeDatum(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: TZ,
  }).format(d);
}

export function tijd(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TZ,
  }).format(d);
}

// Kortst mogelijke datum, bijv. "5-9". Voor plekken waar alleen de volgorde
// telt en de maand er in cijfers bij mag, zoals de kalenderticker.
export function dagMaand(d: Date): string {
  return `${d.getUTCDate()}-${d.getUTCMonth() + 1}`;
}

// Compacte datum binnen een jaar-context, bijv. "za 16 mei".
export function datumInJaar(d: Date): string {
  return new Intl.DateTimeFormat("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: TZ,
  })
    .format(d)
    .replace(/\./g, "");
}

// De server draait op UTC, de wedstrijd op Nederlandse tijd. `new Date()`
// levert hier dus 13:30 terwijl het langs de baan half vier is - twee uur
// verschil in de zomer. Alles wat met "nu" te maken heeft moet daarom
// expliciet in de Nederlandse tijdzone gerekend worden, niet met de klok van
// de machine waarop dit toevallig draait.
const TIJDZONE = "Europe/Amsterdam";

/** Minuten sinds middernacht, in Nederlandse tijd. */
export function minutenNu(): number {
  const delen = new Intl.DateTimeFormat("nl-NL", {
    timeZone: TIJDZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
  const [uur, minuut] = delen.split(":").map(Number);
  return uur * 60 + minuut;
}

/** Vandaag als UTC-middernacht, volgens de Nederlandse kalender. */
export function vandaagNL(): Date {
  const delen = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIJDZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [jaar, maand, dag] = delen.split("-").map(Number);
  return new Date(Date.UTC(jaar, maand - 1, dag));
}

// De datum van de wedstrijddag: tot 05:00 's nachts is het nog gisteren.
//
// Om middernacht sprong het live-scherm op 2 oktober naar de wedstrijd van
// zaterdag, terwijl in Staphorst de trucks nog reden - een avondwedstrijd
// loopt geregeld over twaalven heen. Voor alles wat met "de wedstrijd van
// vandaag" te maken heeft geldt daarom deze datum, niet de kalenderdatum.
export const DAG_WISSELT_OM_UUR = 5;

export function wedstrijddagNL(): Date {
  const delen = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIJDZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(Date.now() - DAG_WISSELT_OM_UUR * 60 * 60 * 1000));
  const [jaar, maand, dag] = delen.split("-").map(Number);
  return new Date(Date.UTC(jaar, maand - 1, dag));
}

// De klok als minuten sinds het begin van de wedstrijddag: na middernacht telt
// hij door - 00:30 is 24:30 - tot de dag om 05:00 wisselt. Zo staat een klasse
// van 21:30 om half één nog gewoon "geweest", en niet "nog te komen".
export function minutenNuWedstrijddag(): number {
  const m = minutenNu();
  return m < DAG_WISSELT_OM_UUR * 60 ? m + 24 * 60 : m;
}

// Nu, als plaatselijke tijd opgeslagen alsof het UTC is - zoals TRS het in
// `bijgewerkt` doet. Dan kun je die twee gewoon van elkaar aftrekken.
export function nuAlsTRS(): Date {
  return new Date(wedstrijddagNL().getTime() + minutenNuWedstrijddag() * 60 * 1000);
}

// De plaats uit een wedstrijdnaam: "Tzum (za)" -> "Tzum".
//
// TRS zet de dag van een tweedaagse tussen haakjes achter de plaats. Waar de
// datum er al naast staat is dat dubbelop, dus dan mag het weg. Bewaar de
// volledige naam wel als bijschrift.
export function plaatsVan(naam: string): string {
  return naam.replace(/\s*\(.*$/, "").trim() || naam;
}

// Het werkelijke moment waarop een wedstrijd begint, als ISO-tekst.
//
// `datum` is UTC-middernacht van de wedstrijddag en `aanvang` bevat alleen een
// tijd; samen vormen ze een wandkloktijd in Nederland. Die moet naar een echt
// moment omgerekend worden, anders telt een klok er in de zomer twee uur naast.
// Zonder aanvangstijd rekenen we vanaf middernacht — dat is bij lang niet elke
// wedstrijd ingevuld, en dan is de dag nauwkeuriger dan de bron.
export function startMoment(datum: Date, aanvang: Date | null): string {
  const uur = aanvang ? aanvang.getUTCHours() : 0;
  const minuut = aanvang ? aanvang.getUTCMinutes() : 0;
  const wandklok = new Date(
    Date.UTC(datum.getUTCFullYear(), datum.getUTCMonth(), datum.getUTCDate(), uur, minuut),
  );
  return new Date(wandklok.getTime() - zoneVerschilMs(wandklok)).toISOString();
}

export type Aftelstand = { dagen: number; uren: number; minuten: number; verstreken: boolean };

// De klok telt in dagen, uren en minuten. Seconden zouden onrustig staan voor
// iets dat weken vooruit ligt, en de aanvangstijd in TRS is bij lang niet elke
// wedstrijd ingevuld — dan is de minuut al nauwkeuriger dan de bron.
//
// Staat hier en niet bij het component, omdat de server hem ook aanroept: die
// rekent de beginstand uit zodat de eerste render van de browser er niet van
// afwijkt.
export function berekenStand(startISO: string, nu: number = Date.now()): Aftelstand {
  const doel = new Date(startISO).getTime();
  const over = doel - nu;
  if (!Number.isFinite(doel) || over <= 0) {
    return { dagen: 0, uren: 0, minuten: 0, verstreken: true };
  }
  const minuten = Math.floor(over / 60000);
  return {
    dagen: Math.floor(minuten / 1440),
    uren: Math.floor((minuten % 1440) / 60),
    minuten: minuten % 60,
    verstreken: false,
  };
}

// Hoeveel loopt de Nederlandse klok voor op UTC, op dit moment in het jaar?
// Via Intl, zodat zomer- en wintertijd vanzelf goed gaan.
function zoneVerschilMs(moment: Date): number {
  const delen = new Intl.DateTimeFormat("en-US", {
    timeZone: TIJDZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(moment)
    .reduce<Record<string, string>>((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});

  const alsUTC = Date.UTC(
    Number(delen.year),
    Number(delen.month) - 1,
    Number(delen.day),
    Number(delen.hour) % 24,
    Number(delen.minute),
    Number(delen.second),
  );
  return alsUTC - moment.getTime();
}

// Sleutel per maand (yyyy-mm) om wedstrijden te groeperen.
export function maandSleutel(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
